import { memo, useRef } from "react";
import type { CompressImage } from "store/features/compress/types";
import { Status } from "./Status/Status";
import { Updates } from "./Updates/Updates";
import { Title } from "./Title/Title";
import { useInterSectionObserver } from "client/hooks/utils/useInterSectionObserver";
import { getFCUrl } from "client/utils/getFCUrl";

export interface PreviewImageProps {
    image: CompressImage;
    index: number;
    handleSelect: (id: string) => void;
    handleDelete: (id: string) => void;
    handleDownload: (image: CompressImage) => void;
    handleSettingsClick: (image: CompressImage) => void;
    isSelected: boolean;
}

export const PreviewImage = memo(
    // memo is used to prevent unnecessary rerenders
    ({
        image,
        handleDelete,
        handleSelect,
        isSelected,
        handleDownload,
        handleSettingsClick,
        index,
    }: PreviewImageProps) => {
        const ref = useRef<HTMLDivElement>(null);
        const isIntersecting = useInterSectionObserver(ref);

        return (
            <div
                className={`group cursor-pointer relative rounded-xl  flex items-center justify-center bg-irGray-200 dark:bg-transparent p-2 text-white font-semibold ${
                    isSelected
                        ? `lg:outline lg:outline-4 outline-irBlue-400 dark:outline-darkPrimary-100`
                        : "border-transparent"
                } dark:border dark:border-darkSurface-100 lg:dark:border-none`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(image.id);
                }}
                ref={ref}
                data-testid={`preview-image-${index}-container`}
            >
                <div className="relative w-full object-cover">
                    <img
                        src={isIntersecting ? getFCUrl(image.src) : ""}
                        key={`${image.id}-preview-image`}
                        className="rounded-xl object-cover mx-auto"
                        style={{ minHeight: "180px" }}
                        loading="lazy"
                        data-testid={`preview-${index}-image`}
                    />

                    <div className="absolute inset-0 flex flex-col bg-black/40 rounded-xl p-2">
                        <Title image={image} handleDelete={handleDelete} index={index} />
                        <Status image={image} handleSettingsClick={handleSettingsClick} index={index} />
                        <Updates image={image} handleDownload={handleDownload} index={index} />
                    </div>
                </div>
            </div>
        );
    },
    (left, right) => {
        if (JSON.stringify(left.image) !== JSON.stringify(right.image) || left.isSelected !== right.isSelected)
            return false;

        return true;
    },
);
